import { Flex, Box, Text, Anchor, Button, Divider } from "@mantine/core";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import MobileHamburger from "./MobileHamburger";
import { useAuth } from "../../hooks/useAuth";
import { useEffect } from "react";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";

export default function LandingLayout() {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(`${path}/`);

  useEffect(() => {
    trpc.maintenance.get
      .query()
      .then((res) => {
        const maintenanceEnabled = !!res?.webMaintenanceEnabled;

        if (maintenanceEnabled && location.pathname !== "/maintenance") {
          navigate("/maintenance", { replace: true });
        }

        if (!maintenanceEnabled && location.pathname === "/maintenance") {
          navigate("/", { replace: true });
        }
      })
      .catch((err) => {
        handleTrpcError(err);
      });
  }, [location.pathname, navigate]);

  return (
    <Flex direction="column" style={{ minHeight: "100vh" }}>
      {/* Sticky Header */}
      <Box
        component="header"
        py="md"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          backdropFilter: "blur(10px)",
          borderBottom: "1px solid #eee",
          width: "100%",
        }}
      >
        <Flex align="center" justify="space-between" px="lg" maw={1280} mx="auto">
          {/* Left: Logo */}
          <Anchor component={Link} to="/" c="slate.6" size="sm" style={{ textDecoration: "none" }}>
            <Text fw={800} size="xl" c="primary.6" style={{ letterSpacing: "-0.5px" }}>
              GT Setu
            </Text>
          </Anchor>

          {/* Center: Navigation */}
          <Flex gap="2rem" visibleFrom="sm">
            <Anchor
              component={Link}
              to="/about"
              c={isActive("/about") ? "primary.6" : "slate.6"}
              fw={isActive("/about") ? 700 : 500}
              style={{ textDecoration: "none" }}
            >
              About
            </Anchor>
            <Anchor
              component={Link}
              to="/pricing"
              c={isActive("/pricing") ? "primary.6" : "slate.6"}
              fw={isActive("/pricing") ? 700 : 500}
              style={{ textDecoration: "none" }}
            >
              Pricing
            </Anchor>
            <Anchor
              component={Link}
              to="/discover"
              c={isActive("/discover") ? "primary.6" : "slate.6"}
              fw={isActive("/discover") ? 700 : 500}
              style={{ textDecoration: "none" }}
            >
              Discover
            </Anchor>

            <Anchor
              component={Link}
              to="/support"
              c={isActive("/support") ? "primary.6" : "slate.6"}
              fw={isActive("/support") ? 700 : 500}
              style={{ textDecoration: "none" }}
            >
              Help & Support
            </Anchor>
          </Flex>

          {/* Right: Auth actions */}
          <Flex gap="1rem" align="center">
            {/* Desktop auth buttons */}

            <Flex gap="1rem" visibleFrom="sm">
              <Button component={Link} to="/login" variant="default" radius="lg">
                Log In
              </Button>
              <Button component={Link} to="/signup" radius="lg">
                Sign Up
              </Button>
            </Flex>

            {/* Mobile hamburger */}
            <MobileHamburger isAuthenticated={isAuthenticated} />
          </Flex>
        </Flex>
      </Box>

      <Box
        component="main"
        style={{
          flex: 1,
          width: "100%",
        }}
      >
        <Outlet />
      </Box>

      <Box component="footer" py={50} px="xl" bg="primary.0">
        <Flex justify="space-between" wrap="wrap" gap="3rem" maw={1280} mx="auto">
          <Box style={{ flex: 1, minWidth: 280, maxWidth: 350 }}>
            <Flex align="center" gap="sm" mb="md">
              <img
                src="/GT-logo.png"
                alt="GT Setu Logo"
                style={{
                  width: 90,
                  height: 90,
                  objectFit: "contain",
                }}
              />
              <Text fw={700} size="xl" c="primary.6" style={{ letterSpacing: "-0.5px" }}>
                GT Setu
              </Text>
            </Flex>
            <Text fw={600} c="primary.6" size="sm" mb="xs">
              A Global Trade Sphere Initiative
            </Text>
            <Text size="sm" c="slate.6" lh={1.6}>
              Trusted, verified global business network for secure collaboration.
            </Text>
          </Box>

          <Box style={{ minWidth: 150 }}>
            <Text fw={600} mb="md" size="sm" c="dark">
              Company
            </Text>
            <Flex direction="column" gap="xs">
              <Anchor component={Link} to="/about" c="slate.6" size="sm" style={{ textDecoration: "none" }}>
                About Us
              </Anchor>
              <Anchor component={Link} to="/pricing" c="slate.6" size="sm" style={{ textDecoration: "none" }}>
                Pricing
              </Anchor>
            </Flex>
          </Box>

          <Box style={{ minWidth: 150 }}>
            <Text fw={600} mb="md" size="sm" c="dark">
              Support
            </Text>
            <Flex direction="column" gap="xs">
              <Anchor component={Link} to="/support" c="slate.6" size="sm" style={{ textDecoration: "none" }}>
                Help Center
              </Anchor>
            </Flex>
          </Box>

          <Box style={{ minWidth: 150 }}>
            <Text fw={600} mb="md" size="sm" c="dark">
              Legal
            </Text>
            <Flex direction="column" gap="xs">
              <Anchor component={Link} to="/privacy" c="slate.6" size="sm" style={{ textDecoration: "none" }}>
                Privacy Policy
              </Anchor>
              <Anchor component={Link} to="/terms" c="slate.6" size="sm" style={{ textDecoration: "none" }}>
                Terms of Service
              </Anchor>
              <Anchor component={Link} to="/compliance" c="slate.6" size="sm" style={{ textDecoration: "none" }}>
                Compliance
              </Anchor>
            </Flex>
          </Box>
        </Flex>

        <Divider my={30} color="gray.3" />

        <Flex justify="space-between" align="center" wrap="wrap" gap="md" maw={1280} mx="auto">
          <Text size="sm" c="slate.6">
            © {new Date().getFullYear()} GT Setu. All rights reserved.
          </Text>

          <Flex gap="lg">
            <Anchor
              href="https://linkedin.com"
              target="_blank"
              c="slate.6"
              size="sm"
              style={{ textDecoration: "none" }}
            >
              LinkedIn
            </Anchor>
            <Anchor href="https://twitter.com" target="_blank" c="slate.6" size="sm" style={{ textDecoration: "none" }}>
              Twitter
            </Anchor>
          </Flex>
        </Flex>
      </Box>
    </Flex>
  );
}
